<template>
    <!-- 头部：移动端增加菜单按钮 -->
    <header class="header">
        <div class="header-left" @click="toggleMenu" v-show="isMobile">
            <i class="el-icon-menu"></i>
        </div>
        <div class="header-title">优优洗车</div>
    </header>

    <!-- 侧边菜单容器：移动端抽屉式（默认展示） -->
    <!-- 新增外层容器包裹，用于隐藏滚动条 -->
    <div class="menu-scroll-wrapper" :class="{ 'mobile-wrapper': isMobile, 'desktop-wrapper': !isMobile }">
        <div
            class="menu-container"
            :class="{
                'menu-show': isMenuShow,
                'menu-hide': !isMenuShow,
                'mobile-menu': isMobile,
                'desktop-menu': !isMobile
            }"
        >
            <!-- 空菜单提示 -->
            <div class="empty-menu" v-if="!menuItemList.length">
                <i class="el-icon-menu"></i>
                <p>暂无可用菜单</p>
            </div>

            <el-menu
                v-else
                router
                :unique-opened="true"
                :default-active="activeMenuPath"
                class="elMenu"
                background-color="#2d3a4b"
                text-color="#fff"
                active-text-color="#fff"
                @select="handleSelect"
                :collapse="isMenuCollapse"
                collapse-transition
            >
                <template v-for="(item, index) in menuItemList" :key="index">
                    <!-- 有子菜单的项 -->
                    <el-submenu
                        :index="item.path"
                        v-if="item.children && item.children.length"
                        class="menu-submenu"
                    >
                        <template #title>
                            <i :class="item.meta?.icon || 'el-icon-location'"></i>
                            <span>{{ item.meta?.title || '未命名菜单' }}</span>
                        </template>
                        <el-menu-item
                            :index="ele.path"
                            v-for="(ele, idx) in item.children"
                            :key="idx"
                            :route="ele"
                            class="menu-subitem"
                        >
                            <i :class="ele.meta?.icon || 'el-icon-menu'"></i>
                            <template #title>{{ ele.meta?.title || '未命名子菜单' }}</template>
                        </el-menu-item>
                    </el-submenu>

                    <!-- 无子菜单的项 -->
                    <el-menu-item
                        :index="item.path"
                        :route="item"
                        v-else
                        class="menu-mainitem"
                    >
                        <i :class="item.meta?.icon || 'el-icon-menu'"></i>
                        <template #title>{{ item.meta?.title || '未命名菜单' }}</template>
                    </el-menu-item>
                </template>
            </el-menu>
        </div>
    </div>

    <!-- 移动端遮罩层（仅菜单显示且非默认状态时显示） -->
    <div
        class="mask"
        v-if="isMenuShow && isMobile && !isDefaultMenuState"
        @click="toggleMenu"
    ></div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";

export default {
    name: "AppSidebar",
    setup() {
        const router = useRouter();
        const store = useStore();

        // 状态管理 - 修改：移动端默认展示菜单
        const menuItemList = ref([]);
        const isMobile = ref(false);
        const isMenuShow = ref(true); // 初始值改为true，默认展示
        const isMenuCollapse = ref(false);
        const isDefaultMenuState = ref(true); // 标记是否为默认展示状态

        // 计算当前激活的菜单路径
        const activeMenuPath = computed(() => {
            return router.currentRoute.path || "";
        });

        // 初始化菜单列表
        const initMenuList = () => {
            try {
                // 过滤有权限的菜单
                const routes = router.getRoutes().filter((val) => {
                    return val.meta && val.meta.auth;
                });

                // 去重并排序（如果有排序字段）
                menuItemList.value = routes
                    .filter(item => item.path) // 过滤空路径
                    .sort((a, b) => (a.meta?.sort || 999) - (b.meta?.sort || 999));

                // 初始化默认激活菜单
                if (menuItemList.value.length && !store.state.activeMenu) {
                    store.dispatch("asyncSetActiveMenu", menuItemList.value[0]);
                }
            } catch (error) {
                console.error("初始化菜单失败：", error);
                ElMessage.error("菜单加载失败，请刷新页面");
            }
        };

        // 菜单选择事件
        const handleSelect = (index, indexPath, item) => {
            // 存储激活菜单
            if (item?.route) {
                store.dispatch("asyncSetActiveMenu", item.route);
            }

            // 移动端选择菜单后：不再自动收起，保持展示状态
            // isMenuShow.value = false; // 注释掉这行，取消自动收起
            isDefaultMenuState.value = false; // 标记为非默认状态
        };

        // 切换菜单显示/隐藏（移动端）
        const toggleMenu = () => {
            isMenuShow.value = !isMenuShow.value;
            isDefaultMenuState.value = false; // 切换后不再是默认状态

            // 移动端切换菜单时控制body滚动
            if (isMobile.value) {
                document.body.style.overflow = isMenuShow.value ? "auto" : "hidden";
            }
        };

        // 检测屏幕尺寸 - 修改：移动端默认展示菜单
        const checkScreenSize = () => {
            const width = window.innerWidth;
            const newIsMobile = width < 768;

            // 屏幕尺寸变化时重置菜单状态
            if (newIsMobile !== isMobile.value) {
                isMobile.value = newIsMobile;

                if (isMobile.value) {
                    isMenuShow.value = true; // 移动端默认展示
                    isMenuCollapse.value = false;
                    document.body.style.overflow = "auto"; // 默认展示时允许滚动
                    isDefaultMenuState.value = true; // 标记为默认状态
                } else {
                    isMenuShow.value = true;
                    isMenuCollapse.value = false;
                    document.body.style.overflow = "auto";
                    isDefaultMenuState.value = false;
                }
            }
        };

        // 监听路由变化（移动端不再自动收起菜单）
        watch(
            () => router.currentRoute.path,
            () => {
                if (isMobile.value) {
                    // 注释掉自动收起逻辑，保持菜单展示
                    // isMenuShow.value = false;
                    // document.body.style.overflow = "auto";
                    isDefaultMenuState.value = false;
                }
            },
            { immediate: true }
        );

        // 生命周期
        onMounted(() => {
            initMenuList();
            checkScreenSize();
            window.addEventListener("resize", checkScreenSize);

            // 监听路由变化重新初始化菜单（处理动态路由）
            router.beforeEach((to, from, next) => {
                initMenuList();
                next();
            });
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", checkScreenSize);
            document.body.style.overflow = "auto"; // 恢复滚动
        });

        return {
            menuItemList,
            isMobile,
            isMenuShow,
            isMenuCollapse,
            isDefaultMenuState, // 新增：传递给模板控制遮罩层
            activeMenuPath,
            handleSelect,
            toggleMenu,
        };
    },
};
</script>

<style scoped>
/* 基础样式 */
.header {
    height: 100px;
    line-height: 100px;
    background-color: #2d3a4b;
    font-size: 30px;
    font-weight: bold;
    text-align: center;
    color: #fff;
    position: relative;
    padding: 0 20px;
    box-sizing: border-box;
    transition: all 0.3s ease;
}

/* 空菜单样式 */
.empty-menu {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;
    padding: 20px;
    text-align: center;
}

.empty-menu i {
    font-size: 48px;
    margin-bottom: 16px;
    color: #555;
}

/* ========== 核心修改：滚动条隐藏容器 ========== */
/* 外层滚动容器 - 隐藏滚动条但保留滚动功能 */
.menu-scroll-wrapper {
    position: relative;
    overflow: hidden; /* 隐藏滚动条 */
    height: 100%;
    width: 100%;
    background-color: #2d3a4b;
}

/* 内层菜单容器 - 可滚动，宽度超出外层（隐藏滚动条） */
.menu-container {
    background-color: #2d3a4b;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    overflow-y: auto; /* 允许垂直滚动 */
    overflow-x: hidden; /* 禁止水平滚动 */
    width: calc(100% + 20px); /* 宽度超出外层，隐藏滚动条 */
    scrollbar-width: none; /* Firefox 隐藏滚动条 */
    -ms-overflow-style: none; /* IE/Edge 隐藏滚动条 */
    /* 滚动顺滑度优化 */
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch; /* 移动端弹性滚动 */
}

/* Chrome/Safari/Opera 隐藏滚动条 */
.menu-container::-webkit-scrollbar {
    display: none !important; /* 完全隐藏滚动条 */
    width: 0 !important;
    height: 0 !important;
}

/* 移动端滚动容器适配 */
.mobile-wrapper {
    position: fixed;
    top: 60px;
    left: 0;
    width: 240px;
    height: calc(100vh - 60px);
    z-index: 999;
}

/* 桌面端滚动容器适配 */
.desktop-wrapper {
    width: 100%;
    height: 100%;
}

.elMenu {
    height: 100%;
    border-right: none;
}

/* ========== 菜单层级样式 ========== */
/* 一级菜单（主菜单） */
.menu-mainitem {
    position: relative;
}

/* 子菜单容器 */
::v-deep .menu-submenu .el-submenu__title {
    position: relative;
}

/* 子菜单项 - 核心缩进样式 */
.menu-subitem {
    position: relative;
}

/* 子菜单整体缩进 + 背景色区分 */
::v-deep .menu-submenu .el-menu {
    background-color: #232f40 !important;
    padding-left: 0 !important;
}

/* ========== 移动端适配样式 (768px以下) ========== */
@media screen and (max-width: 767px) {
    /* 头部适配 */
    .header {
        height: 60px;
        line-height: 60px;
        font-size: 18px;
        text-align: left;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .header-left {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.1);
        cursor: pointer;
        transition: background-color 0.2s ease;
    }

    .header-left:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .header-title {
        flex: 1;
        text-align: center;
        font-size: 17px;
    }

    /* 移动端菜单容器 - 默认展示 */
    .mobile-menu {
        transform: translateX(0); /* 默认展示 */
        box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    }

    /* 菜单显示/隐藏 */
    .menu-show {
        transform: translateX(0);
    }

    .menu-hide {
        transform: translateX(-100%);
    }

    /* 菜单样式优化 */
    .elMenu {
        font-size: 14px;
    }

    /* 一级菜单项 */
    ::v-deep .menu-mainitem .el-menu-item {
        padding: 0 20px !important;
        height: 48px !important;
        line-height: 48px !important;
        transition: background-color 0.2s ease;
    }

    /* 子菜单标题 */
    ::v-deep .menu-submenu .el-submenu__title {
        padding: 0 20px !important;
        height: 48px !important;
        line-height: 48px !important;
    }

    /* 子菜单项 - 缩进20px，区分一级菜单 */
    ::v-deep .menu-subitem .el-menu-item {
        padding: 0 20px 0 40px !important; /* 左侧缩进20px */
        height: 48px !important;
        line-height: 48px !important;
        border-left: 2px solid transparent;
    }

    /* 子菜单激活时左侧边框标记 */
    ::v-deep .menu-subitem .el-menu-item.is-active {
        border-left: 2px solid #66b1ff;
        background-color: #1e2938 !important;
    }

    /* 菜单图标间距 */
    ::v-deep .menu-mainitem i,
    ::v-deep .menu-submenu .el-submenu__title i {
        margin-right: 10px;
        font-size: 16px;
    }

    /* 子菜单图标更小，间距更小 */
    ::v-deep .menu-subitem i {
        margin-right: 8px;
        font-size: 14px;
        color: #a0aec0;
    }

    /* 遮罩层 */
    .mask {
        position: fixed;
        top: 60px;
        left: 0;
        width: 100vw;
        height: calc(100vh - 60px);
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 998;
        backdrop-filter: blur(2px);
        animation: maskFadeIn 0.3s ease;
    }

    @keyframes maskFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
}

/* ========== 平板适配 (768px - 1024px) ========== */
@media screen and (min-width: 768px) and (max-width: 1024px) {
    .header {
        height: 80px;
        line-height: 80px;
        font-size: 24px;
    }

    .desktop-wrapper {
        width: 180px;
        height: calc(100vh - 80px);
    }

    .desktop-menu {
        width: calc(180px + 20px); /* 宽度超出外层，隐藏滚动条 */
    }

    .elMenu {
        font-size: 14px;
    }

    /* 一级菜单项 */
    ::v-deep .menu-mainitem .el-menu-item {
        padding: 0 15px !important;
        height: 45px !important;
        line-height: 45px !important;
    }

    /* 子菜单标题 */
    ::v-deep .menu-submenu .el-submenu__title {
        padding: 0 15px !important;
        height: 45px !important;
        line-height: 45px !important;
    }

    /* 子菜单项 - 缩进15px */
    ::v-deep .menu-subitem .el-menu-item {
        padding: 0 15px 0 30px !important; /* 左侧缩进15px */
        height: 45px !important;
        line-height: 45px !important;
    }

    /* 菜单图标间距 */
    ::v-deep .menu-mainitem i,
    ::v-deep .menu-submenu .el-submenu__title i {
        margin-right: 8px;
        font-size: 15px;
    }

    ::v-deep .menu-subitem i {
        margin-right: 6px;
        font-size: 13px;
        color: #a0aec0;
    }
}

/* ========== 大屏样式 (1025px以上) ========== */
@media screen and (min-width: 1025px) {
    .header-left {
        display: none;
    }

    .desktop-wrapper {
        width: 200px;
        height: calc(100vh - 100px);
    }

    .desktop-menu {
        width: calc(200px + 20px); /* 宽度超出外层，隐藏滚动条 */
    }

    .elMenu {
        font-size: 15px;
    }

    /* 一级菜单项 */
    ::v-deep .menu-mainitem .el-menu-item {
        padding: 0 20px !important;
        height: 50px !important;
        line-height: 50px !important;
    }

    /* 子菜单标题 */
    ::v-deep .menu-submenu .el-submenu__title {
        padding: 0 20px !important;
        height: 50px !important;
        line-height: 50px !important;
    }

    /* 子菜单项 - 缩进25px，更明显的层级区分 */
    ::v-deep .menu-subitem .el-menu-item {
        padding: 0 20px 0 45px !important; /* 左侧缩进25px */
        height: 50px !important;
        line-height: 50px !important;
    }

    /* 菜单图标间距 */
    ::v-deep .menu-mainitem i,
    ::v-deep .menu-submenu .el-submenu__title i {
        margin-right: 12px;
        font-size: 16px;
    }

    ::v-deep .menu-subitem i {
        margin-right: 10px;
        font-size: 14px;
        color: #a0aec0;
    }
}

/* ========== 通用优化 ========== */
::v-deep .el-menu {
    --el-menu-text-color: #e5e9f2;
    --el-menu-active-text-color: #fff;
    --el-menu-hover-text-color: #fff;
    --el-menu-bg-color: #2d3a4b;
    --el-menu-hover-bg-color: #1f2d3d;
    --el-menu-active-bg-color: #1f2d3d;
}

/* 一级菜单激活样式 */
::v-deep .menu-mainitem .el-menu-item.is-active {
    background-color: #1f2d3d !important;
    border-left: 3px solid #409eff;
}

/* 子菜单箭头样式 */
::v-deep .menu-submenu .el-submenu__icon-arrow {
    color: #999;
    margin-right: 5px;
}

/* 子菜单hover效果优化 */
::v-deep .menu-subitem .el-menu-item:hover {
    background-color: #1e2938 !important;
    color: #fff !important;
}

/* ========== 小屏幕适配 (480px以下) ========== */
@media screen and (max-width: 479px) {
    .mobile-wrapper {
        width: 220px;
    }

    .mobile-menu {
        width: calc(220px + 20px); /* 宽度超出外层，隐藏滚动条 */
    }

    .header-title {
        font-size: 16px;
    }

    /* 子菜单项缩进调整 */
    ::v-deep .menu-subitem .el-menu-item {
        padding: 0 16px 0 36px !important;
    }

    ::v-deep .menu-mainitem .el-menu-item,
    ::v-deep .menu-submenu .el-submenu__title {
        padding: 0 16px !important;
    }
}

/* ========== 滚动体验优化 ========== */
/* 禁止选中文本，提升滚动体验 */
.menu-container {
    user-select: none;
    -webkit-user-select: none;
}

/* 移动端触摸滚动优化 */
@media (pointer: coarse) {
    .menu-container {
        overscroll-behavior-y: contain; /* 防止滚动穿透 */
    }
}
</style>
